import { useSupabase, useTenant, useTenantSettings } from "@how/app-kit";
import { Card, Stack, Switch, Text, Title } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ErrorState, LoadingState, PageHeader } from "@how/ui";
import { MessagingCard } from "./MessagingCard";
import { PaymentsCard } from "./PaymentsCard";
import { PlanCard } from "./PlanCard";
import { SignInAddressesCard } from "./SignInAddressesCard";
import { TimeZoneCard } from "./TimeZoneCard";

/**
 * Church settings. The MFA policy is one of the two directly client-writable columns (ADR-0018), gated by
 * settings.manage in RLS. The time zone is the owner's alone (settings.time_zone), through set_tenant_time_zone.
 */
export function SettingsPage() {
  const supabase = useSupabase();
  const tenant = useTenant();
  const settings = useTenantSettings();
  const queryClient = useQueryClient();
  const tenantId = tenant.status === "resolved" ? tenant.tenant.tenantId : "";
  const update = useMutation({
    mutationFn: async (requireMfa: boolean) => {
      const { error, count } = await supabase
        .from("tenant_settings")
        .update({ require_mfa_for_admins: requireMfa }, { count: "exact" })
        .eq("tenant_id", tenantId);
      if (error) throw error;
      if (count === 0) throw new Error("not permitted");
    },
    onSuccess: (_data, requireMfa) => {
      notifications.show({
        color: "teal",
        title: "Saved",
        message: requireMfa
          ? "Administrators must now verify with an authenticator app."
          : "Administrators can sign in with a password alone.",
      });
      void queryClient.invalidateQueries({ queryKey: ["tenant-settings"] });
      void queryClient.invalidateQueries({ queryKey: ["authorize"] });
    },
    onError: () => {
      notifications.show({
        color: "red",
        title: "Not saved",
        message: "You do not have permission to change this.",
      });
    },
  });
  return (
    <>
      <PageHeader
        title="Settings"
        description="How your church account works, and who can change it."
      />
      <PlanCard />
      <TimeZoneCard />
      <SignInAddressesCard />
      <Card>
        {settings.isPending ? (
          <LoadingState rows={2} />
        ) : settings.isError ? (
          <ErrorState
            message="Could not load settings."
            onRetry={() => {
              void settings.refetch();
            }}
          />
        ) : (
          <Stack gap="sm">
            <Title order={4}>Sign-in security</Title>
            <Switch
              label="Require two-step verification for administrators"
              description="Owners and administrators must confirm sign-in with an authenticator app before they can change anything."
              checked={settings.data.require_mfa_for_admins}
              disabled={update.isPending}
              onChange={(event) => {
                update.mutate(event.currentTarget.checked);
              }}
            />
            <Text size="xs" c="dimmed">
              Enforced by the database on every action, not just at sign-in.
            </Text>
          </Stack>
        )}
      </Card>
      <PaymentsCard />
      <MessagingCard />
    </>
  );
}
